import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useMsal } from "@azure/msal-react";
import { toast } from "react-toastify";
import ResourcesService from "../Services/ResourcesService";
import { Image } from "./Image";
import ResourceRentModel from "../Models/ResourceRentModel";
import DatePicker, { registerLocale } from "react-datepicker";
import { format, parseJSON } from "date-fns";
import nlBE from "date-fns/locale/nl-BE";
import "react-datepicker/dist/react-datepicker.css";
import GraphService from "../Services/GraphService";
import { User } from "@microsoft/microsoft-graph-types";
import ResourceModel from "../Models/ResourceModel";

interface ResourceModalProps {
  resource: ResourceModel;
  afterSave: Function;
  afterCancel: Function;
}

const ResourceRentingModal = (props: ResourceModalProps) => {
  const { instance } = useMsal();

  registerLocale("nlBE", nlBE);

  const [visible, setVisible] = useState<boolean>(true);
  const [resource, setResource] = useState<ResourceModel>(props.resource);
  const [resourceRents, setResourceRents] = useState<ResourceRentModel[]>([]);
  const [currentUser, setCurrentUser] = useState<User>();
  const [warehouseOperator, setWarehouseOperator] = useState<boolean>(false);
  const [from, setFrom] = useState<Date | null>(new Date());
  const [until, setUntil] = useState<Date | null>(new Date());

  const resourcesService = useMemo(
    () => new ResourcesService(instance),
    [instance],
  );

  const graphService = useMemo(() => new GraphService(instance), [instance]);

  const loadImage = useCallback(
    async (id: number) => {
      try {
        const image = await resourcesService.getImage(id);

        setResource((prevState) => ({
          ...prevState,
          image: image,
        }));
      } catch (error) {
        toast.error("Fout bij het ophalen van de data.");
      }
    },
    [resourcesService],
  );

  const loadRents = useCallback(
    async (id: number) => {
      try {
        const data = await resourcesService.getRents(id);

        setResourceRents(data);
      } catch (error) {
        toast.error("Fout bij het ophalen van de data.");
      }
    },
    [resourcesService],
  );

  const loadCurrentUser = useCallback(async () => {
    try {
      const user = await graphService.getUserDetails();
      setCurrentUser(user);
    } catch (err) {
      console.log(err);
      toast.error("Fout bij het ophalen van de huidige gebruiker.");
    } finally {
    }
  }, [graphService]);

  const modalSave = async () => {
    try {
      if (from === null) {
        toast.error("De van datum is niet ingevuld");
        return;
      }
      if (until === null) {
        toast.error("De tot datum is niet ingevuld");
        return;
      }
      const error = await resourcesService.upsertRent({
        resourceId: props.resource.id,
        createdBy: currentUser?.displayName ?? undefined,
        from: format(from, "yyyy-MM-dd") + "T00:00:00.000Z",
        until: format(until, "yyyy-MM-dd") + "T00:00:00.000Z",
        isReturned: false,
      });

      if (error === undefined) {
        setVisible(false);
        props.afterSave();
      } else {
        toast.error(error);
      }
    } catch (error) {
      toast.error("Fout bij het ophalen van de data.");
    }
  };

  const saveReturned = async () => {
    try {
      if (props.resource.id) {
        await resourcesService.saveReturnedRent(props.resource.id);

        setVisible(false);
        props.afterSave();
      }
    } catch (error) {
      toast.error("Fout bij het ophalen van de data.");
    }
  };

  const modalToggle = () => {
    setVisible((visible) => {
      return !visible;
    });

    props.afterCancel();
  };

  useEffect(() => {
    if (props.resource.id !== undefined) {
      loadImage(props.resource.id);
      loadRents(props.resource.id);
    }
  }, [props.resource.id, loadImage, loadRents]);

  useEffect(() => {
    async function loadWarehouseOperator() {
      try {
        const warehouseOperator =
          await graphService.hasRole("warehouse.operator");

        setWarehouseOperator(warehouseOperator);
      } catch (error) {
        toast.error("Fout bij het ophalen van de data.");
      }
    }

    loadWarehouseOperator();
  }, [graphService]);

  useEffect(() => {
    loadCurrentUser();
  }, [loadCurrentUser]);

  useEffect(() => {
    if (from !== null && until !== null && from > until) {
      setUntil(from);
    }
  }, [from]);

  return (
    <>
      <Modal isOpen={visible} toggle={modalToggle} size="xl">
        <ModalHeader toggle={modalToggle}>
          {resource?.code} - {resource?.name}
        </ModalHeader>
        <ModalBody>
          <form>
            <div className="form-group" style={{ whiteSpace: "pre-wrap" }}>
              {resource?.description}
            </div>
            <div className="form-group">
              <Image image={resource?.image} />
            </div>
            <hr />
            {resourceRents.map((record) => (
              <div className="row" key={record.id}>
                <div className="col">
                  {!record.from
                    ? ""
                    : format(parseJSON(record.from), "dd/MM/yyyy")}{" "}
                  -{" "}
                  {!record.until
                    ? ""
                    : format(parseJSON(record.until), "dd/MM/yyyy")}{" "}
                  <span style={{ textWrap: "nowrap" }}>
                    door {record.createdBy}
                  </span>
                </div>
              </div>
            ))}
            <hr />
            <div className="row">
              <div className="col">
                <div className="form-group">
                  <label className="my-1 mr-2">Van</label>
                  <DatePicker
                    locale="nlBE"
                    selected={from}
                    onChange={(e) => {
                      setFrom(e);
                    }}
                    dateFormat="dd/MM/yyyy"
                    customInput={
                      <input
                        type="text"
                        className="form-control my-1 mr-sm-2"
                        title="Van"
                      />
                    }
                  />
                </div>
              </div>
              <div className="col">
                <div className="form-group">
                  <label className="my-1 mr-2">tot</label>
                  <DatePicker
                    locale="nlBE"
                    selected={until}
                    onChange={(e) => setUntil(e)}
                    dateFormat="dd/MM/yyyy"
                    customInput={
                      <input
                        type="text"
                        className="form-control my-1 mr-sm-2"
                        title="Van"
                      />
                    }
                  />
                </div>
              </div>
            </div>
          </form>
        </ModalBody>
        <ModalFooter>
          {warehouseOperator ? (
            <>
              <Button color="success" onClick={saveReturned}>
                Ingeleverd
              </Button>{" "}
            </>
          ) : null}
          <Button color="primary" onClick={modalSave}>
            Reserveren
          </Button>{" "}
          <Button color="secondary" onClick={modalToggle}>
            Sluiten
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ResourceRentingModal;
